/**
 * Hand.tsx — your private hand at the bottom of the screen. Cards are
 * draggable to any zone, AND the hand strip itself is a drop target so
 * cards can be returned here from abyss / banishment / a realm.
 */

import { useDroppable } from '@dnd-kit/core';
import type { CardData, CardId, PlayerId } from '../types.js';
import { Card } from './Card.js';

type HandProps = {
  owner: PlayerId;
  cardIds: readonly CardId[];
  cards: ReadonlyMap<string, CardData>;
  imageUrl: (cardId: string) => string;
  onCardContextMenu?: ((cardId: string, e: React.MouseEvent) => void) | undefined;
};

export function Hand({ owner, cardIds, cards, imageUrl, onCardContextMenu }: HandProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: `hand-${owner}`,
    data: { kind: 'hand', owner },
  });
  return (
    <div
      ref={setNodeRef}
      className={`flex justify-center gap-2 p-5 bg-cosmos-900/60 backdrop-blur transition ${
        isOver ? 'ring-2 ring-cyan-300 ring-inset' : ''
      }`}
      data-owner={owner}
    >
      {cardIds.length === 0 ? (
        <div className="h-44 flex items-center text-cosmos-400 text-sm italic">
          (your hand is empty — drop a card here to add it)
        </div>
      ) : (
        cardIds.map((cardId, idx) => (
          // The hover-bump previously lived on a wrapper div, but its
          // transform fought dnd-kit's translate during drag and made the
          // dragged card jitter. Hover affordance is now a ring on the
          // Card itself (no transform competition).
          <Card
            // biome-ignore lint/suspicious/noArrayIndexKey: hand is index-positioned and may contain duplicate card_ids; the composite key disambiguates.
            key={`${cardId}-${idx}`}
            cardId={cardId}
            card={cards.get(cardId)}
            imageUrl={imageUrl(cardId)}
            draggableId={`hand-${owner}-${cardId}-${idx}`}
            dragData={{ cardId, source: { kind: 'hand', owner } }}
            onContextMenu={onCardContextMenu ? (e) => onCardContextMenu(cardId, e) : undefined}
            className="transition hover:ring-2 hover:ring-amber-300"
          />
        ))
      )}
    </div>
  );
}
